import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';

/**
 * Lifecycle of a statement run. Stored as a varchar (not a DB enum) so new
 * states never need an ALTER on the column.
 */
export enum StatementRunStatus {
	/** Created (by the daily schedule or by hand) and waiting for the worker. */
	PENDING = 'PENDING',
	/** Claimed by the run processor; statements are being generated. */
	PROCESSING = 'PROCESSING',
	/** All customers processed; counts are final. */
	COMPLETE = 'COMPLETE',
	/** Aborted by an unrecoverable error (see errorMessage). */
	FAILED = 'FAILED',
}

/** How a run came to exist — mirrors the statement-level generation source. */
export enum StatementRunSource {
	/** Created by the daily schedule evaluation (PD-14044). */
	SCHEDULED = 'SCHEDULED',
	/** Queued by a user via "Add Statement Run". */
	MANUAL = 'MANUAL',
}

/**
 * PD-14044 — one statement run for one issuing company: "generate statements
 * for this company's customers, stamped as of this date". Runs are created
 * PENDING (by the daily schedule evaluation when a company's recurrence rule
 * lands on today, or by the manual "Add Statement Run" action) and a BullMQ
 * worker periodically picks up PENDING runs and processes them — so a run
 * queued while the worker is down is simply processed on the next tick.
 */
@Index('status', ['status'], {})
@Index('fk_company__id', ['fkCompanyId'], {})
@Entity('statement_run', {}, { label: 'Statement Run' })
export class StatementRunEntity extends BaseEntity {
	/** The issuing company whose customers this run generates statements for. */
	@Column('int', { name: 'fk_company__id', unsigned: true })
	public fkCompanyId!: number;

	/**
	 * When set, the run is scoped to ONE client (PD-14075): created by the
	 * daily evaluation for clients with their own statement day override.
	 * Null = company-wide run (all opted-in customers without an override).
	 */
	@Column('int', { name: 'fk_client__id', nullable: true, unsigned: true })
	public fkClientId!: number | null;

	/** The point-in-time stamp every statement in the run is generated as of. */
	@Column('datetime', { name: 'asOfDate' })
	public asOfDate!: Date;

	/** Period window start; null = from the beginning of each client's history. */
	@Column('datetime', { name: 'periodStartDate', nullable: true })
	public periodStartDate!: Date | null;

	/** Period window end; null = through the as-of date. */
	@Column('datetime', { name: 'periodEndDate', nullable: true })
	public periodEndDate!: Date | null;

	/** See {@link StatementRunStatus}. */
	@Column('varchar', { name: 'status', length: 32 })
	public status!: string;

	/** See {@link StatementRunSource}. */
	@Column('varchar', { name: 'source', length: 32 })
	public source!: string;

	/** Statements generated by this run (final once COMPLETE). */
	@Column('int', { name: 'statementsGenerated', default: 0 })
	public statementsGenerated!: number;

	/**
	 * Customers skipped because their statement had no activity and no open
	 * balance — generating an empty statement for every dormant customer
	 * would bury the real ones.
	 */
	@Column('int', { name: 'statementsSkipped', default: 0 })
	public statementsSkipped!: number;

	/**
	 * Statements emailed to the customer's email on file (PD-14045).
	 * Customers without an email keep their saved statement with a null
	 * distributedDate — generated but not distributed.
	 */
	@Column('int', { name: 'statementsDistributed', default: 0 })
	public statementsDistributed!: number;

	/** When processing finished (COMPLETE or FAILED). */
	@Column('datetime', { name: 'completedDate', nullable: true })
	public completedDate!: Date | null;

	/** Why the run FAILED; null otherwise. */
	@Column('text', { name: 'errorMessage', nullable: true })
	public errorMessage!: string | null;

	@ManyToOne(() => CompanyEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;
}
